import { useState } from "react";
import { useIntl } from "react-intl";
import { IForm } from "../../";
import { lastSaveConflict } from "../../../../utils/lastSaveConflict";
import VehicleChildForm from "./components/vehicleChildForm";
import { getRequiredFields } from "./utils/required-fields-by-step";
import DriverChildForm from "./components/driverChildForm";
import TowTruckContactsChildForm from "./components/towTruckContactsChildForm";
import DealershipChildForm from "./components/dealershipChildForm";
import ServiceChildForm from "./components/serviceChildForm";
import ErrorDialog from "../../../../components/dialogs/ErrorDialog";
import SuccessDialog from "../../../../components/dialogs/SuccessDialog";
import { formatFieldLabel } from "./utils/fields-labels";
import { Occurrence } from "../../../../models/occurrence";
import { FinalizeOccurrenceButton } from "../../dialogs/finalizationReasonDialog";
import {
  OccurrenceStepEnum,
  OccurrenceStepLabelIdMap,
} from "../../../../enums/occurrenceStep";

const ScreeningForm = (props: IForm) => {
  const intl = useIntl();
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
  const [changeStepErrorMessage, setChangeStepErrorMessage] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  const requiredServiceFields = getRequiredFields(
    props.occurrence?.occurrenceType === "P" ? "SCREENING_PREVENTIVE_CHECKUP" : "SCREENING",
    props.occurrence?.occurrenceType
  );

  // TASK 26658: Adicionar o dealershipDn como campo obrigatório para triagem de chamadas para ocorrências do tipo Guincho ou Socorro
  const isDealershipDnRequired = ['W', 'A'].includes(props.occurrence?.occurrenceType ?? '');

  const isDealership = props.occurrence?.occurrenceType === 'D';
  const serviceHiddenFields = isDealership
    ? ['estimatedTime','partOrderStatus','driverCheckInDriver','mechanicLocation','towTruckLocation','literatureDiagnosis','literatureTroubleshooting','tasNumber','tasStatus','checklist','solutionProposed','criticality','createdBy','dtStart','dtEnd','modules','estimateTimeRepair','vehiclePayloadType','vehicleMaximumPayload','vehicleCriticalPayload']
    : ['estimatedTime','timeOpenProtocol','country','driverCheckInDriver','mechanicLocation','towTruckLocation','literatureDiagnosis','literatureTroubleshooting','tasNumber','tasStatus','checklist','solutionProposed'];

  return (
    <div>
      <div className="text-size-h4 font-bold">{intl.formatMessage({ id: "intl-msg:kanban.form.screeningForm.title" })}</div>
      <div className="text-color-status-resting text-size-14">
        {intl.formatMessage({ id: "intl-msg:kanban.form.screeningForm.description" })}
      </div>

      <VehicleChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={["assetId", "protocolNumber"]}
        disabledFields={["vehicleChassis"]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      {!isDealership && (
      <DriverChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[]}
        disabledFields={[]}
        disabledAllFields={true}
        requiredFields={[]}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />
      )}

      <TowTruckContactsChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={[]}
        disabledFields={[]}
        requiredFields={[]}
        disabledAllFields={true}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <DealershipChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={isDealershipDnRequired ? ["dealershipRepresentative"] : ["dealershipId","dealershipRepresentative"]}
        disabledFields={[]}
        disabledAllFields={isDealershipDnRequired ? props.disabledAllFields : true}
        requiredFields={isDealershipDnRequired ? ["dealershipDn"] : []}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        hasError={hasError}
      />

      <ServiceChildForm
        occurrence={props.occurrence}
        setOccurrenceValue={props.setOccurrenceValue}
        hiddenFields={serviceHiddenFields}
        disabledFields={[
          "estimateTimeRepair",
          "criticality",
          "createdBy",
          "occurrenceType",
          "hasLink",
          "mainOccurrence",
          "source",
          "vehiclePayloadType",
          "vehicleMaximumPayload",
          "vehicleCriticalPayload",
          "modules",
          "serviceBaySchedule",
          "dynamicsOccurrenceCode",
          "osNumber",
          "osDtOpenAt",
        ]}
        requiredFields={requiredServiceFields}
        disabledAllFields={props.disabledAllFields}
        step={props.step}
        setOccurrenceStepValue={props.setOccurrenceStepValue}
        partners={props.partners}
        upsertDispatch={props.upsertDispatch}
        cancelDispatch={props.cancelDispatch}
        removeDispatch={props.removeDispatch}
        persona={props.persona}
        hasError={hasError}
        scheduling={props.scheduling}
      />

      <hr className="my-6" />

      {props.persona !== "MANAGER" &&
        !props.finalized &&
        !props.hiddenSaveButton && (
          <div className="flex justify-end gap-3">
            <button
              className={`btn btn-primary ${
                props.loading ? "btn-loading disabled" : ""
              }`}
              type="button"
              onClick={async () => {
                if (!props.occurrence) return;
                const isValid = props.isValidStep(
                  [...requiredServiceFields, isDealershipDnRequired ? "dealershipDn" : ""].filter(e => e !== ""), 
                  props.occurrence
                );
                setHasError(!isValid);
                if (!isValid) {
                  setShowValidationErrorDialog(true);
                  return;
                }
                const success = await props.save(props.occurrence);
                if (success) {
                  setShowSuccessDialog(true);
                } else if (!lastSaveConflict.current) {
                  setShowErrorDialog(true);
                }
              }}
              disabled={props.loading || props.disabledAllFields}
            >
              {intl.formatMessage({ id: "intl-msg:occurrence.save" })}
            </button>
            
            <FinalizeOccurrenceButton
              occurrence={props.occurrence}
              finishOccurrence={props.finishOccurrence}
              disabledAllFields={props.disabledAllFields}
              persona={props.persona}
              stepId={props.step?.id}
            />
          </div>
        )}

      <ErrorDialog
        show={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.screeningForm.saveErrorTitle" })}
        body={intl.formatMessage({ id: "intl-msg:kanban.form.screeningForm.saveErrorBody" })}
      />

      <ErrorDialog
        show={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.screeningForm.missingPartTitle" })}
        body={intl.formatMessage({ id: "intl-msg:kanban.form.screeningForm.missingPartBody" })}
      />

      <ErrorDialog
        show={!!changeStepErrorMessage}
        onClose={() => setChangeStepErrorMessage(null)}
        title={intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepTitle" })}
        body={changeStepErrorMessage ?? ""}
      />

      <SuccessDialog
        show={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={intl.formatMessage({ id: "intl-msg:kanban.form.screeningForm.successTitle" })}
        body={
          <div className="space-y-4">
            <div>{intl.formatMessage({ id: "intl-msg:kanban.form.screeningForm.successBody" })}</div>
            {!hasError && (
              <div>
                {intl.formatMessage({ id: "intl-msg:kanban.form.screeningForm.advanceQuestion" }, { step: intl.formatMessage({ id: OccurrenceStepLabelIdMap.DISPLACEMENT }) })}
              </div>
            )}
            {hasError && (
              <>
                <div>
                  {intl.formatMessage({ id: "intl-msg:occurrence.step.errorChangeStepBody" })}
                </div>
                <div>{intl.formatMessage({ id: "intl-msg:occurrence.step.requiredFields" })}</div>
                <ul className="list-disc list-inside">
                  {props
                    .getNotFilledRequiredFields(
                      [...requiredServiceFields, isDealershipDnRequired ? "dealershipDn" : ""].filter(e => e !== ""),
                      props.occurrence ?? ({} as Occurrence)
                    )
                    .map((field: string) => (
                      <li key={field}>
                        {formatFieldLabel(intl, field)}
                      </li>
                    ))}
                </ul>
              </>
            )}
          </div>
        }
        footer={
          <button
            className="btn btn-primary"
            disabled={hasError || !props.changeStep || !props.occurrence}
            onClick={async () => {
              if (!props.summaryStep?.id || !props.occurrence) return;
              try {
                const nextStep = props.occurrence?.occurrenceType === "P"
                  ? OccurrenceStepEnum.SECOND_DIAGNOSIS
                  : OccurrenceStepEnum.DISPLACEMENT;

                await props.changeStep!(
                  props.occurrence!,
                  props.summaryStep.id,
                  nextStep
                );
                setShowSuccessDialog(false);
                props.onClose();
              } catch (error: any) {
                setShowSuccessDialog(false);
                setChangeStepErrorMessage(error?.message ?? intl.formatMessage({ id: "intl-msg:kanban.form.screeningForm.changeStepError" }));
              }
            }}
          >
            {intl.formatMessage({ id: "intl-msg:occurrence.step.advanceStepTitle" })}
          </button>
        }
      />

    </div>
  );
};

export default ScreeningForm;
